import React, { useState } from "react";
import {
  ChevronRight,
  User,
  Lock,
  LogOut,
  Activity,
  Monitor,
  Folder,
  Shield,
  Info,
  FileText,
  Scale,
  Cookie,
  Trash2,
  UserX,  
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import LogoutModal from "./LogoutModal";
import ErrorModal from "../Modal/ErrorModal";

const SettingsMenu = ({ activeSection, onSectionChange, isMobile }) => {
  const { logout } = useAuth();

  const [logoutModal, setLogoutModal] = useState({
    isOpen: false,
    type: null,
    isLoading: false,
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const menuSections = [
    {
      id: "profile",
      icon: User,
      label: "Profile & Account",
      description: "Manage how others see you",
    },
    {
      id: "activity",
      icon: Activity,
      label: "Your Activity",
      description: "Manage your account activity",
    },
    {
      id: "collections",
      icon: Folder,
      label: "Collections",
      description: "Organize your saved content",
    },
    {
      id: "privacy",
      icon: Lock,
      label: "Privacy & Interactions",
      description: "Control who can reach you",
    },
    {
      id: "account-privacy",
      icon: Shield,
      label: "Account Privacy",
      description: "Control your account privacy",
    },
    {
      id: "blocked-accounts",
      icon: UserX,
      label: "Blocked Accounts",
      description: "Manage accounts you've blocked",
    },
    {
      id: "about",
      icon: Info,
      label: "About",
      description: "Learn about Wryven",
    },
    {
      id: "privacy-policy",
      icon: FileText,
      label: "Privacy Policy",
      description: "Our privacy practices",
    },
    {
      id: "terms-of-use",
      icon: Scale,
      label: "Terms of Use",
      description: "Terms and conditions",
    },
    {
      id: "cookies-policy",
      icon: Cookie,
      label: "Cookies Policy",
      description: "Our cookies practices",
    },
    {
      id: "delete-account",
      icon: Trash2,
      label: "Delete Account",
      description: "Permanently delete your account",
    },
  ];

  const logoutOptions = [
    {
      id: "logout",
      icon: LogOut,
      label: "Log Out",
      description: "Sign out of this device",
      isDestructive: false,
    },
    {
      id: "logoutAll",
      icon: Monitor,
      label: "Log Out of All Devices",
      description: "Sign out from all devices",
      isDestructive: true,
    },
  ];

  const handleLogoutClick = (type) => {
    setLogoutModal({
      isOpen: true,
      type: type,
      isLoading: false,
    });
  };

  const handleLogoutConfirm = async () => {
    setLogoutModal((prev) => ({ ...prev, isLoading: true }));

    try {
      const isLogoutAll = logoutModal.type === "logoutAll";
      await logout(isLogoutAll);

      setLogoutModal({
        isOpen: false,
        type: null,
        isLoading: false,
      });
    } catch (error) {
      setLogoutModal({
        isOpen: false,
        type: null,
        isLoading: false,
      });
      setErrorModal({
        isOpen: true,
        message: "We couldn't log you out. Please try again.",
      });
    }
  };

  const handleLogoutClose = () => {
    if (!logoutModal.isLoading) {
      setLogoutModal({
        isOpen: false,
        type: null,
        isLoading: false,
      });
    }
  };

  if (isMobile) {
    return (
      <>
        <div className="bg-white">
          {menuSections.map((section) => (
            <button
              key={section.id}
              onClick={() => onSectionChange(section.id)}
              className="w-full flex items-center justify-between px-4 py-4 border-b border-gray-100 hover:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                <section.icon className="w-6 h-6 text-black" />
                <div className="text-left">
                  <p className="font-medium text-black">{section.label}</p>
                  <p className="text-xs text-black opacity-70">
                    {section.description}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-black opacity-40" />
            </button>
          ))}

          <div className="sm:border-t sm:border-gray-200 sm:pt-4">
            {logoutOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleLogoutClick(option.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-50"
              >
                <div className="flex items-center space-x-3">
                  {/* Forced text-red-600 */}
                  <option.icon className="w-6 h-6 text-red-600" />
                  <div className="text-left">
                    <p className="font-semibold text-red-600">{option.label}</p>
                    <p className="text-xs text-black opacity-70">
                      {option.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <LogoutModal
          isOpen={logoutModal.isOpen}
          type={logoutModal.type}
          onClose={handleLogoutClose}
          onConfirm={handleLogoutConfirm}
          isLoading={logoutModal.isLoading}
        />
        <ErrorModal
          isOpen={errorModal.isOpen}
          message={errorModal.message}
          onClose={() => setErrorModal({ isOpen: false, message: "" })}
        />
      </>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 sticky top-8">
        <nav className="space-y-1">
          {menuSections.map((section) => (
            <button
              key={section.id}
              onClick={() => onSectionChange(section.id)}
              className={`w-full flex items-center p-4 rounded-lg text-left transition-all ${
                activeSection === section.id
                  ? "bg-gray-100 text-black"
                  : "hover:bg-gray-50 text-black"
              }`}
            >
              <section.icon className="w-5 h-5 mr-3 text-black" />
              <div className="flex-1">
                <p className="font-medium text-black">{section.label}</p>
                <p className="text-xs text-black opacity-70 mt-0.5">
                  {section.description}
                </p>
              </div>
            </button>
          ))}

          <div className="border-t border-gray-200 mt-4 pt-4">
            {logoutOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleLogoutClick(option.id)}
                className="w-full flex items-center p-4 rounded-lg text-left transition-all hover:bg-red-50"
              >
                {/* Forced text-red-600 for both icon and label */}
                <option.icon className="w-5 h-5 mr-3 text-red-600" />
                <div className="flex-1">
                  <p className="font-semibold text-sm text-red-600">
                    {option.label}
                  </p>
                  <p className="text-xs text-black opacity-70 mt-0.5">
                    {option.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </nav>
      </div>

      <LogoutModal
        isOpen={logoutModal.isOpen}
        type={logoutModal.type}
        onClose={handleLogoutClose}
        onConfirm={handleLogoutConfirm}
        isLoading={logoutModal.isLoading}
      />
    </>
  );
};

export default SettingsMenu;
